import React, { useState } from 'react';
import { Link } from 'react-router';

import Icon from '~/components/Icon';
import Login from '~/components/Login';
import Modal from '~/components/Modal';
import { useAuth } from '~/contexts/AuthContext';
import useIsMobile from '~/hooks/useIsMobile';
import { cn } from '~/utils/cn';

import Button from './Button';

interface SignInProps {
  variant?: 'button' | 'link';
  className?: string;
  /**
   * Homepage top bar: on mobile the username drops onto its own line under the
   * scoreboard link instead of sitting next to it (where a long name overflows).
   * Only affects the authenticated state — the Sign in button stays on the right.
   */
  stackOnMobile?: boolean;
}

function SignIn({ variant = 'button', className, stackOnMobile }: SignInProps) {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const { isAuthenticated, user, isLoading: isAuthenticating } = useAuth();
  const isMobile = useIsMobile();

  if (isAuthenticating) {
    return null;
  }

  if (isAuthenticated) {
    const nickname =
      user.nickname.length > 20
        ? `${user.nickname.slice(0, 20)}...`
        : user.nickname;

    return (
      <Link
        to="/me"
        className={cn(
          'h-base flex min-w-0 items-center gap-2',
          stackOnMobile && 'max-md:order-2 max-md:w-full',
          className
        )}
      >
        <Icon name="account" size={6} className="mb-px shrink-0" />
        <span data-cy="auth-user" className="truncate font-medium text-white">
          {nickname}
        </span>
      </Link>
    );
  }

  return (
    <>
      {variant === 'button' ? (
        <Button
          variant="green"
          size={isMobile ? 'small' : 'large'}
          onClick={() => setIsLoginModalOpen(true)}
          className={className}
        >
          Sign In
        </Button>
      ) : (
        <button
          type="button"
          onClick={() => {
            setIsLoginModalOpen(true);
          }}
          className={cn('h-base flex items-center gap-2', className)}
        >
          <Icon name="account" size={6} className="mb-px" />
          <span className="font-medium text-white">Sign In</span>
        </button>
      )}

      <Modal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        size="small"
        disabled={isAuthenticated}
      >
        <Login />
      </Modal>
    </>
  );
}

export default SignIn;
